import { fieldTypes } from "@/components/TemplateBuilder/config";
import ability from '@/libs/acl/ability';

export default class FormNormalizer {
  constructor(vm, templateFields, options = {}) {
    this.vm = vm;
    this.templateFields = templateFields;
    this.options = options;
    this.customComponents = options.customComponents || {};
    this.model = options.model || {};
  }

  convertFieldType(type) {
    const fieldType = fieldTypes.find(fType => fType.type === type);
    return fieldType.data_type;
  }

  buildGroup() {
    return {
      component: 'div',
      class: 'mt-3',
      children: []
    };
  }

  buildButton(options = {}) {
    return {
      component: 'button',
      name: options.name,
      type: options.type,
      disabled: options.disabled,
      id: options.id,
      children: [
        {
          component: 'span',
          children: options.text
        }
      ],
      class: Array.isArray(options.classes) ? options.classes.join(' ') : [options.classes].join(' ')
    };
  }

  requiredFields() {
    return [];
  }

  isFieldVisible(field, sectionFields, indexInSection, answers) {
    if (!field || !sectionFields || !Array.isArray(sectionFields)) {
      return true;
    }

    const visibilityConditions = Array.isArray(field.show_if) ? field.show_if : [];
    const conditionLogic = field.show_logic === "any" ? "any" : "all";

    if (visibilityConditions.length === 0) {
      return true;
    }

    const fieldIndexMap = new Map(
      sectionFields.map((sectionField, index) => [sectionField.item_id, index])
    );

    const getFieldAnswer = (fieldItemId) => {
      const fieldIndex = fieldIndexMap.get(fieldItemId);

      if (fieldIndex === undefined) {
        return { found: false, value: undefined, index: -1, field: null };
      }

      const referencedField = sectionFields[fieldIndex];

      if (!referencedField || !referencedField.name) {
        return { found: false, value: undefined, index: -1, field: null };
      }

      const answerValue = answers && typeof answers === 'object'
        ? answers[referencedField.name]
        : undefined;

      return {
        found: true,
        index: fieldIndex,
        field: referencedField,
        value: answerValue,
      };
    };

    const normalizeToArray = (value) => {
      if (Array.isArray(value)) {
        return value;
      }
      if (value === undefined || value === null || value === "") {
        return [];
      }
      return [value];
    };

    const areValuesEqual = (valueA, valueB) => {
      const arrayA = normalizeToArray(valueA);
      const arrayB = normalizeToArray(valueB);

      if (Array.isArray(valueA) || Array.isArray(valueB)) {
        const serializedA = arrayA.map(item => JSON.stringify(item));
        const serializedB = arrayB.map(item => JSON.stringify(item));

        if (serializedA.length !== serializedB.length) {
          return false;
        }

        const setB = new Set(serializedB);
        return serializedA.every(item => setB.has(item));
      }

      return String(valueA) === String(valueB);
    };

    const arrayContainsValue = (array, searchValue) => {
      const normalizedArray = array || [];
      const serializedArray = normalizedArray.map(item => JSON.stringify(item));
      const serializedSearch = JSON.stringify(searchValue);
      return serializedArray.includes(serializedSearch);
    };

    const evaluateCondition = (condition) => {
      if (!condition || !condition.field) {
        return false;
      }

      const fieldAnswer = getFieldAnswer(condition.field);

      if (!fieldAnswer.found) {
        return false;
      }

      if (fieldAnswer.index >= indexInSection) {
        return false;
      }

      const answerValue = fieldAnswer.value;
      const referencedField = fieldAnswer.field;
      const isMultiSelectField = referencedField &&
        (referencedField.type === 'multiple_select' || referencedField.type === 'checkbox');

      if (condition.op === "==") {
        if (isMultiSelectField && Array.isArray(answerValue)) {
          return arrayContainsValue(answerValue, condition.value);
        }
        return areValuesEqual(answerValue, condition.value);
      }

      return false;
    };

    const conditionResults = visibilityConditions.map(evaluateCondition);

    if (conditionLogic === "any") {
      return conditionResults.some(result => result === true);
    }

    return conditionResults.every(result => result === true);
  }

  convert() {
    const schema = [];
    if (!this.templateFields || !Array.isArray(this.templateFields)) {
      return schema;
    }

    const model = this.model || {};
    const fields = this.templateFields
      .map((field, index) => {
        if (!field) return null;
        if (!this.isFieldVisible(field, this.templateFields, index, model)) {
          return null;
        }

        const attributes = {};

        if (field.type === 'select' || field.type === 'multiple_select') {
          attributes.placeholder = this.vm.$t('maintenance.choose_an_option');
        }

        let validationRules = [];
        const isRequired = !!field.options?.required;

        if (isRequired) {
          validationRules.push('required');
        } else {
          validationRules.push('optional');
        }

        let minValue = null;
        let maxValue = null;
        if (field.type === 'number') {
          validationRules.push('number');

          if (field.options?.fieldTypeCfg?.data) {
            const numberConfig = field.options.fieldTypeCfg.data;
            if (typeof numberConfig === 'object' && !Array.isArray(numberConfig)) {
              minValue = numberConfig.min;
              maxValue = numberConfig.max;

              if (minValue !== null && minValue !== undefined && minValue !== "") {
                validationRules.push(`min:${minValue},value`);
              }

              if (maxValue !== null && maxValue !== undefined && maxValue !== "") {
                validationRules.push(`max:${maxValue},value`);
              }
            }
          }
        }

        let fieldLabel = field.name;
        if (
          field.type === 'number' &&
          minValue !== null && minValue !== undefined && minValue !== "" &&
          maxValue !== null && maxValue !== undefined && maxValue !== ""
        ) {
          fieldLabel = `${field.name} (${minValue}, ${maxValue})`;
        }

        const fieldSchema = {
          id: field.item_id,
          name: field.name,
          label: fieldLabel,
          type: this.convertFieldType(field.type),
          disabled: this.options.disabled === true,
          validation: validationRules.join('|'),
          validationName: fieldLabel,
          options: ['select', 'multiple_select', 'checkbox'].includes(field.type)
            ? field.options.fieldTypeCfg.data
            : undefined,
          ...attributes,
        };

        if (field.type === 'number') {
          fieldSchema.validationMessages = fieldSchema.validationMessages || {};

          if (minValue !== null && minValue !== undefined && minValue !== "") {
            fieldSchema.validationMessages.min = this.vm.$t('maintenance.number_config.min_value')
              .replace('{field}', field.name)
              .replace('{min}', minValue);
          }

          if (maxValue !== null && maxValue !== undefined && maxValue !== "") {
            fieldSchema.validationMessages.max = this.vm.$t('maintenance.number_config.max_value')
              .replace('{field}', field.name)
              .replace('{max}', maxValue);
          }

          // Native browser number input config
          if (minValue !== null && minValue !== undefined && minValue !== "") {
            fieldSchema.min = minValue;
          }

          if (maxValue !== null && maxValue !== undefined && maxValue !== "") {
            fieldSchema.max = maxValue;
          }

          fieldSchema.step = 'any';
        }

        if (field.type === 'multiple_select') {
          fieldSchema.type = 'multipleSelect';

          fieldSchema.fieldOptions = field.options;
          if (!this.model[field.name] || !Array.isArray(this.model[field.name])) {
            this.model[field.name] = [];
          } else {
            this.model[field.name] = this.model[field.name].filter(
              val => val !== null && val !== undefined && val !== ''
            );
          }
        }

        console.log(JSON.stringify(this.model[field.name]));

        if (field.type === 'media' || field.type === 'signature') {
          fieldSchema.type = 'component';
          fieldSchema.component = this.customComponents.FileUploadWithPreview;
          fieldSchema.onUpdateFiles = this.options.onUpdateFiles;
          fieldSchema.existingFiles = this.model[field.name] || [];
            fieldSchema.allowPaste = field.type === "media";

        if (field.type === "media") {
          const mediaConfig = field.options?.fieldTypeCfg?.data || {};

          fieldSchema.minFiles =
            mediaConfig.min !== undefined &&
            mediaConfig.min !== null &&
            mediaConfig.min !== ""
              ? Number(mediaConfig.min)
              : null;

          fieldSchema.maxFiles =
            mediaConfig.max !== undefined &&
            mediaConfig.max !== null &&
            mediaConfig.max !== ""
              ? Number(mediaConfig.max)
              : null;
        }

        }

        return fieldSchema;
      })
      .filter(field => field !== null);

    schema.push(...this.requiredFields());
    schema.push(...fields);

    const buttonGroup = this.buildGroup();
    const submitButton = {
      id: 'save-maintenance-form',
      name: 'submit',
      type: 'submit',
      text: 'Save',
      classes: ['btn', 'btn-primary', 'text-uppercase', 'mr-1'],
      disabled: this.options.disabled === true,
    };

    buttonGroup.children.push(this.buildButton(submitButton));

    if (this.options.showValidateBtn && ability.can('validate_form', 'MaintenanceForm')) {
      const validateButton = {
        id: 'validate-maintenance-form',
        name: 'validate',
        type: 'button',
        text: this.vm.$t('actions.validate'),
        classes: ['btn', 'btn-success', 'text-uppercase'],
        disabled: false,
      };
      buttonGroup.children.push(this.buildButton(validateButton));
    }

    schema.push(buttonGroup);

    return schema;
  }
}
